import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Target, ArrowLeft } from "lucide-react";

export default function Terms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-50 bg-gray-50/90 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Target className="w-4 h-4 text-[#111]" />
            </div>
            <span className="font-bold text-[#111]">GolfFlowPro</span>
          </div>
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#555] hover:text-[#111]" data-testid="terms-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </header>

      <main className="px-6 py-10">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#111] mb-1">Terms of Service</h1>
          <p className="text-[#555] mb-10 text-sm">Last updated: March 2026</p>

          <div className="space-y-8 text-[#555] text-[15px] leading-relaxed">
            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">1. Agreement</h2>
              <p>By accessing or using GolfFlowPro ("the Service"), you agree to be bound by these Terms of Service and our <a href="/privacy" className="text-emerald-700 hover:underline">Privacy Policy</a>. If you do not agree, do not use the Service.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">2. About the Service</h2>
              <p>GolfFlowPro is a golf practice planning platform that provides:</p>
              <ul className="space-y-1.5 ml-4 mt-3">
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Personalised daily practice sessions based on time, location, and skill level</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>A guided practice session experience with structured drills</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Coach-student management tools (drill assignment, compliance tracking)</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Progress tracking and practice analytics</span></li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">3. Accounts</h2>
              <p>To use GolfFlowPro, you must create an account via Google Sign-In. You agree to:</p>
              <ul className="space-y-1.5 ml-4 mt-3">
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Provide accurate information</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Keep your account secure</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Not share your account with others</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Be responsible for all activity under your account</span></li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">4. User Roles</h2>
              <p className="mb-3">GolfFlowPro supports two user roles:</p>
              <ul className="space-y-1.5 ml-4">
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span><strong className="text-[#111]">Student:</strong> Can view their own practice plans, complete sessions, and manage their own data. Students linked to a coach will have their practice compliance visible to that coach.</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span><strong className="text-[#111]">Coach:</strong> Can manage linked students, create and assign drills, and view practice compliance for their own students only. Coaches cannot access data belonging to other coaches or unlinked students.</span></li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">5. Subscription & Payments</h2>
              <p className="mb-3">GolfFlowPro offers a free tier and a paid Pro subscription.</p>
              <ul className="space-y-1.5 ml-4">
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Pro subscription is billed monthly at the displayed rate</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Payments are processed securely via Stripe</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>You may cancel at any time; access continues until the end of the billing period</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>No refunds for partial billing periods</span></li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">6. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul className="space-y-1.5 ml-4 mt-3">
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Use the Service for any unlawful purpose</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Attempt to access data belonging to other users</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Interfere with or disrupt the Service</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Scrape, reverse engineer, or exploit the platform</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Use the Service to harass or harm others</span></li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">7. Intellectual Property</h2>
              <p>All content, features, and functionality of GolfFlowPro are owned by GolfFlowPro and protected by copyright and other intellectual property laws. Drill content created by coaches remains their intellectual property.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">8. Disclaimer</h2>
              <p>The Service is provided "as is" without warranties of any kind. We do not guarantee that:</p>
              <ul className="space-y-1.5 ml-4 mt-3">
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>The Service will be uninterrupted or error-free</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>Practice plans will produce specific golf improvement results</span></li>
                <li className="flex gap-2"><span className="text-emerald-700">&#8226;</span><span>AI-generated recommendations are suitable for your specific situation</span></li>
              </ul>
              <p className="mt-3"><strong className="text-[#111]">Important:</strong> Our practice plans are for general guidance. Consult a qualified golf professional for personalised instruction. Always warm up properly and practise safely.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">9. Limitation of Liability</h2>
              <p>To the maximum extent permitted by Irish and EU law, GolfFlowPro shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">10. Account Termination</h2>
              <p>You may delete your account at any time from your Profile settings. We reserve the right to suspend or terminate accounts that violate these Terms. Upon termination, your data will be handled in accordance with our <a href="/privacy" className="text-emerald-700 hover:underline">Privacy Policy</a>.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">11. Changes to Terms</h2>
              <p>We may update these Terms from time to time. Material changes will be communicated via email or in-app notification. Continued use after changes constitutes acceptance.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">12. Governing Law</h2>
              <p>These Terms are governed by and construed in accordance with the laws of Ireland and applicable European Union regulations, including the General Data Protection Regulation (GDPR).</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-[#111] mb-3">13. Contact</h2>
              <p>For questions about these Terms:</p>
              <p className="mt-2"><strong className="text-[#111]">Email:</strong> <a href="mailto:legal@golfflowpro.com" className="text-emerald-700 hover:underline">legal@golfflowpro.com</a></p>
              <p className="mt-1 text-[#555] text-sm">GolfFlowPro, Ireland</p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <Button onClick={() => navigate("/")} className="bg-emerald-500 hover:bg-emerald-600 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
